import React, { useState, useEffect, useRef } from "react";
import {
  Box, VStack, Heading, Text, useColorModeValue, Button,
  SimpleGrid, HStack, useBreakpointValue, useToast, Tooltip, Spinner, Badge,Flex,
} from "@chakra-ui/react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { StudentInfo, ExamListing } from "../Common/types";
import { FiBookOpen, FiClock, FiCheckCircle, FiLock, FiPlayCircle } from "react-icons/fi";
import { toUtcISOString, parseUtc, fmtLocal } from "../../utils/dates";
import { useLocation } from "react-router-dom";

// ✅ Moved helper functions OUTSIDE the component
const getRelevantExams = (exams: ExamListing[]) => {
  const today = new Date().toDateString();
  return exams.filter(
    exam =>
      new Date(exam.start_time).toDateString() === today &&
      exam.status !== "COMPLETED"
  );
};

const getEarliestPollTime = (exams: ExamListing[], openWindowMinutes: number) =>
  Math.min(
    ...exams.map(
      exam => new Date(exam.start_time).getTime() - openWindowMinutes * 60 * 1000
    )
  );

const StudentExams = () => {
  const [currentStudent, setCurrentStudent] = useState<StudentInfo>();
  const [examListings, setExamListings] = useState<ExamListing[]>([]);
  const [openWindowMinutes, setOpenWindowMinutes] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const toast = useToast();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const cardBg = useColorModeValue("blue.50", "blue.700");

  const textColor = useColorModeValue("blue.800", "blue.50");
  const bannerColor = useColorModeValue("yellow.100", "yellow.700");
  const showBanner = useBreakpointValue({ base: false, md: true });

  const token = localStorage.getItem("token");
  const isLoggedIn = Boolean(token);

  const location = useLocation();
  const isOnStudentExamPage = location.pathname.includes("/student-exams");

  const pollingStartedRef = useRef(false);

  const fetchCurrentStudent = async () => {
    try {
      const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
      const response = await axios.get(`${apiurl}/students-info`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setCurrentStudent(response.data);
    } catch (error) {
      console.error("Error fetching Student Info:", error);
    }
  };

  const fetchExamOpenWindow = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    try {
      const res = await axios.get(`${apiUrl}/config/exam-open-window/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setOpenWindowMinutes(res.data.exam_open_window_minutes || 10);
    } catch (err) {
      console.error("Failed to get exam open window setting", err);
      setOpenWindowMinutes(10);
    }
  };

  const fetchExamListings = async () => {
    setIsLoading(true);
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    try {
      const response = await axios.get(`${apiUrl}/student/exams/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
          // peek at the raw strings straight from the backend
    if (response.data.length) {
      console.log("[RAW]", {
        start: response.data[0].start_time,
        end:   response.data[0].end_time,
      });

      console.log("[fmt]", {
        start: fmtLocal(response.data[0].start_time),
        end:    fmtLocal(response.data[0].end_time),
      });
    }
      setExamListings(response.data);
    } catch (error) {
      console.error("Failed to load exam listings", error);
    }
    setIsLoading(false);
    
    console.log(
  fmtLocal('2025-06-25 04:26:15.170'),   // ← DB format (space)
  fmtLocal('2025-06-25T04:26:15.170'),   // ← ISO without Z
  fmtLocal('2025-06-25T04:26:15.170Z')   // ← ISO with Z
);
  };

  const handleOpenExam = async(exam_id: number) => {

 
    const apiurl = process.env.REACT_APP_API_URL || 'https://default-url.com';
    const response = await axios.post(`${apiurl}/exam-attempt/start/${exam_id}`,null, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    
    const exam_attempt_id = response.data.exam_attempt_id;
    console.log("exam attempt id", exam_attempt_id);
  //  navigate(`/coaching/dashboard/student-exams/exam-lobby/${exam_attempt_id}`);
  handleOpenExamLobby(exam_attempt_id);
  }

  const startPolling = () => {
    if (intervalRef.current !== null) {
      console.warn("Polling is already running — skipping duplicate start.");
      return;
    }
    console.log("The polling has been started");
    fetchExamListings(); // immediate fetch
    intervalRef.current = setInterval(() => {
      if (!document.hidden) {
        fetchExamListings();
      }
    }, 60 * 1000);
  };

  let examWindow: Window | null = null;

  const handleOpenExamLobby = (exam_id: number) => {
    const width = 1200;
    const height = 800;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const url = `${window.location.origin}/exam-window/${exam_id}`;
    const features = `width=${width},height=${height},top=${top},left=${left},` +
                     `resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no`;

/*     if (examWindow && !examWindow.closed) {
    examWindow.focus();
  } else {
    examWindow = window.open(url, "_blank", features);
  } */

   // window.open(url, "_blank", features);
   //    const url = `${window.location.origin}/mock-exam-window/${practice_attempt_id}`;
    window.location.href = url;
  };

  // ✅ Clean polling setup with no infinite loop
  useEffect(() => {
    if (!isOnStudentExamPage || document.hidden || !localStorage.getItem("token")) return;
    
    // ✅ Initial fetch for data
    fetchCurrentStudent();
    fetchExamOpenWindow();
    //fetchExamListings();

    let timeoutId: NodeJS.Timeout | null = null;

    const setupPolling = async () => {
      if (pollingStartedRef.current) {
        console.log("Polling already setup — skipping");
        return;
      }
      pollingStartedRef.current = true;

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      try {
        const res = await axios.get(`${apiUrl}/student/exams/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const listings: ExamListing[] = res.data;
        console.log("Student Exam Polling starting from Set up polling");
        setExamListings(listings);

        const todayExams = getRelevantExams(listings);
        if (todayExams.length === 0) return;

        const now = Date.now();
        const earliestPollTime = getEarliestPollTime(todayExams, openWindowMinutes);
        const timeUntilPollStart = earliestPollTime - now;

        if (timeUntilPollStart > 5 * 60 * 1000) {
          timeoutId = setTimeout(startPolling, timeUntilPollStart - 5 * 60 * 1000);
        } else {
          startPolling();
        }
      } catch (err) {
        console.error("Failed to setup polling", err);
      }
    };

    setupPolling();

    return () => {
      pollingStartedRef.current = false;
      
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    
      if (intervalRef.current !== null) {
        console.log("Cleaning up polling interval");
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOnStudentExamPage, openWindowMinutes]);

  // ✅ Handle tab visibility change
useEffect(() => {
  if (!isOnStudentExamPage) return;

  const handleVisibilityChange = () => {
    if (!document.hidden) {
      fetchExamListings();
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}, [isOnStudentExamPage]);


  const getButtonProps = (exam: ExamListing) => {
    const open = exam.status === "OPEN";
    const ongoing = exam.status === "ONGOING";

    if (ongoing) {
      return {
        label: "Join Ongoing Exam",
        color: "red",
        isDisabled: false
      };
    }

    return {
      label: "Open Exam",
      color: "blue",
      isDisabled: !open
    };
  };

  const getStatusProps = (status: string) => {
    switch (status) {
      case "OPEN":
        return { colorScheme: "green", icon: FiCheckCircle, label: "Open for entry" };
      case "ONGOING":
        return { colorScheme: "red", icon: FiPlayCircle, label: "Exam in progress" };
      case "PUBLISHED":
      default:
        return { colorScheme: "gray", icon: FiLock, label: "Not yet open" };
    }
  };

  return (
    <Box>
      <Box>
        {showBanner ? (
          <Flex
            bgColor={bannerColor}
            p={4}
            height="auto"
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={2}
          >
            <Box>
              <Heading size="sm">Hello {currentStudent?.givenName}!</Heading>
              <Text fontSize="md">
                Checkout your upcoming exams below. Click on Refresh if you are expecting an exam to be shown.
              </Text>
            </Box>
            <Button
              colorScheme="teal"
              alignSelf={{ base: "flex-end", md: "center" }}
              onClick={fetchExamListings}
            >
              Refresh
            </Button>
          </Flex>
        ) : (
          <Flex
            bgColor={bannerColor}
            p={4}
            height="auto"
            justify="space-between"
            align="center"
          >
            <Text fontWeight="semibold" fontSize="md">My Exams</Text>
            <Button colorScheme="teal" onClick={fetchExamListings}>
              Refresh
            </Button>
          </Flex>
        )}
      </Box>


      <Box mx="auto" maxWidth="1200px" color={textColor} p={6}>
{/*         <HStack mb={4} justifyContent="space-between">
          <Heading size="md">Upcoming Exams</Heading>
         
        </HStack> */}

        {isLoading ? (
          <Spinner size="xl" />
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {examListings.map(exam => {
              const { label, color, isDisabled } = getButtonProps(exam);
              const statusProps = getStatusProps(exam.status);

              return (
                <Box
                  key={exam.exam_id}
                  borderWidth="1px"
                  borderRadius="lg"
                  p={5}
                //  bg={isDisabled ? "gray.100" : "white"}
                  bg={cardBg}
                  opacity={isDisabled ? 0.7 : 1}
                  boxShadow="sm"
                  transition="all 0.2s"
                  _hover={{ boxShadow: "md" }}
                >
                  <HStack spacing={3} mb={2}>
                    <FiBookOpen />
                    <Heading size="sm" noOfLines={2}>
                      {exam.title}
                    </Heading>
                  </HStack>

                  <VStack align="start" spacing={1} mt={2} fontSize="sm">
                    <HStack>
                      <FiClock />
                      <Text>Start: {fmtLocal(exam.start_time)}</Text>
                    </HStack>
                    <HStack>
                      <FiClock />
                      <Text>End: {fmtLocal(exam.end_time)}</Text>
                    </HStack>
                  </VStack>

                  <Tooltip label={statusProps.label} placement="top">
                    <Badge
                      mt={3}
                      colorScheme={statusProps.colorScheme}
                      fontSize="0.75em"
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      <HStack spacing={1}>
                        <Box as={statusProps.icon} />
                        <Text>{exam.status}</Text>
                      </HStack>
                    </Badge>
                  </Tooltip>

                  <Button
                    mt={4}
                    colorScheme={color}
                    size="sm"
                    onClick={() => handleOpenExam(exam.exam_id)}
                    isDisabled={isDisabled}
                    width="full"
                  >
                    {label}
                  </Button>
                </Box>
              );
            })}
          </SimpleGrid>
        )}
      </Box>
    </Box>
  );
};

export default StudentExams;
